<template>
  <page>
    <header-component />
    <section>
      <div class="container m:mb-16">
        <div class="flex flex-col justify-between m:flex-row mt-6 l:mt-16">
          <div class="m:w-7/12 l:w-6/12 m:mr-6 pb-8">
            <b-headline type="h1" typography="h15" class="mb-6 m:mb-8">
              {{ headline }}
            </b-headline>
            <p v-html="errorMessage" class="m:text-lg" />

            <button-link v-if="!isGenericError" :href="$options.explorePath" class="block mt-8 m:w-72">
              {{ $t("exploreContent") }}
            </button-link>

            <a v-if="showPlansLink" :href="$options.plansPath" class="block mt-4 underline text-mid-grey">
              {{ $t("plansLink") }}
            </a>
          </div>

          <error-image class="m:w-5/12 hidden m:block" />
        </div>
      </div>

      <error-image class="block m:hidden" />
    </section>
  </page>
</template>

<script>
import Page from "@/shared/page"
import HeaderComponent from "@/features/header/components"
import ErrorImage from "@/pages/error-page/components/error-image"
import ButtonLink from "@/component-library/button-link"
import BHeadline from "@/component-library/b-headline/index"
import { getSearchParam } from "@/utils/location"
import { mapGetters } from "vuex"
import globals from "@/utils/globals"

export default {
  name: "ErrorPage",
  components: {
    BHeadline,
    ButtonLink,
    ErrorImage,
    Page,
    HeaderComponent
  },
  explorePath: globals.routes.explorePath,
  plansPath: globals.routes.plans,
  computed: {
    ...mapGetters(["isUserLoggedIn", "isUserLoggedOut", "isUserPremium", "isUserBasic"]),
    type() {
      return getSearchParam("type")
    },
    isGenericError() {
      return this.errorMessageKey === "error.generic"
    },
    isB2bAudienceError() {
      return this.errorMessageKey === "error.spaceForB2COnly"
    },
    showPlansLink() {
      return !this.isGenericError && !this.isUserPremium && !this.isB2bAudienceError
    },
    headline() {
      const key = `headline.${this.type}`
      return this.$te(key) ? this.$t(key) : this.$t("headline.generic")
    },
    errorMessageKey() {
      const key = `error.${this.type}`
      return this.$te(key) ? key : "error.generic"
    },
    errorMessage() {
      return this.$t(this.errorMessageKey, { totalBooks: globals.totalBooks, accountType: this.userAccountType })
    },
    userAccountType() {
      if (this.isUserPremium) {
        return this.$t("accountType.premium")
      } else if (this.isUserBasic) {
        return this.$t("accountType.basic")
      } else {
        return null
      }
    }
  },
  i18n: {
    messages: {
      en: {
        exploreContent: "Explore Blinkist",
        plansLink: "Check out our plans",
        headline: {
          generic: "Sorry, something went wrong.",
          forbidden: "Access denied",
          invalidInvite: "Sorry, this invite link is invalid.",
          inviteAlreadyUsed: "Sorry, this invite link has already been used.",
          partOfOtherOrganisation: "Seems like you’re already part of another plan.",
          spaceForB2BOnly: "Sorry, this invite link is invalid.",
          spaceForB2COnly: "Sorry, this invite link is invalid.",
          spaceNotFound: "Sorry, this invite link is invalid.",
          onlyForNewUsers: "It looks like like you already have a Blinkist premium subscription.",
          blinkistAccountAlreadyLinked: "Your Blinkist account is already linked to a Go1 account.",
          go1AccountAlreadyLinked: "Your Go1 account was already linked to another Blinkist account.",
          ensureCorrectQrCode: "The QR Code is invalid.",
          go1PortalBlocked: "Your organization's Blinkist access has ended.",
          selfInvite: "You can't invite yourself.",
          emailMismatch: "This invite is not for this email address."
        },
        error: {
          generic:
            "Something unexpected happened, please try again later.<br><br>If the problem persists, feel free to contact us via: <a href='mailto:support@blinkist.com'>support@blinkist.com</a>",
          forbidden:
            "You do not have permissions to view this page.<br><br>If you think you should be able to access the requested page and the problem persists, feel free to contact us via: <a href='mailto:support@blinkist.com'>support@blinkist.com</a>",
          invalidInvite:
            "This invite link is invalid and can’t be used.<br><br>Don’t let that stop you from getting connected to the powerful ideas from {totalBooks}+ nonfiction books and podcasts!",
          inviteAlreadyUsed:
            "This invite link has already been used and is no longer valid.<br><br>Don’t let that stop you from getting connected to the powerful ideas from {totalBooks}+ nonfiction books and podcasts!",
          partOfOtherOrganisation:
            "Unfortunately, you can’t use this invitation if you are already part of a team or shared plan.<br><br>You can, however, still use your current account to get connected to the world around you through powerful ideas from {totalBooks}+ nonfiction books and podcasts!",
          spaceForB2BOnly:
            "This invite is only for people who are from within an organization, unfortunately you can’t access it.<br><br>Don’t let that stop you from <b>using your {accountType} account</b> and getting connected to the world of powerful ideas around you from 5,000+ nonfiction books and podcasts!",
          spaceForB2COnly:
            "This invite is only for people who have a personal account which is not part of their company benefits.<br><br>Don’t let that stop you from <b>using your {accountType} account</b> and getting connected to the world of powerful ideas around you from 5,000+ nonfiction books and podcasts!",
          spaceNotFound:
            "The invite link is invalid and can't be used.<br><br>Don’t let that stop you from using your {accountType} account and getting connected to the world of powerful ideas around you from 5,000+ nonfiction books and podcasts!",
          onlyForNewUsers: "This offer is available to new users only. Sign into your account to continue learning!",
          blinkistAccountAlreadyLinked: "It is not possible to add another Go1 account to your Blinkist account.",
          go1AccountAlreadyLinked: "It is not possible to link your Go1 account to an additional Blinkist account.",
          ensureCorrectQrCode: "Ensure that you are using the QR code from the correct Go1 account and try refreshing the page with the QR code.",
          go1PortalBlocked: "Blinkist access through your organization's Go1 portal has been discontinued, so Premium access cannot be activated. To discuss further, please contact us via support@blinkist.com.",
          selfInvite: "You can't use your own invite code to join your organization.",
          emailMismatch: "This invite was sent to a different email address. Please use the same email address that received the invite."
        },
        accountType: {
          basic: "Basic",
          premium: "Premium"
        }
      },
      de: {
        exploreContent: "Entdecke Blinkist",
        plansLink: "Sieh dir unsere Abos an",
        headline: {
          generic: "Entschuldigung, etwas ist schief gelaufen!",
          forbidden: "Zugriff verweigert",
          invalidInvite: "Dieser Einladungslink ist ungültig.",
          inviteAlreadyUsed: "Dieser Einladungslink wurde bereits verwendet",
          partOfOtherOrganisation: "Offenbar hast du bereits ein anderes Abo.",
          spaceForB2BOnly: "Dieser Einladungslink ist ungültig.",
          spaceForB2COnly: "Dieser Einladungslink ist ungültig.",
          spaceNotFound: "Dieser Einladungslink ist ungültig.",
          onlyForNewUsers: "Es sieht so aus, als hättest du bereits ein Blinkist Premium-Abo.",
          blinkistAccountAlreadyLinked: "Dein Blinkist-Konto ist bereits mit einem Go1-Konto verknüpft.",
          go1AccountAlreadyLinked: "Dein Go1-Konto wurde bereits mit einem anderen Blinkist-Konto verknüpft.",
          ensureCorrectQrCode: "Stelle sicher, dass du den richtigen QR-Code verwendet hast.",
          go1PortalBlocked: "Der Blinkist-Zugang deiner Organisation wurde eingestellt.",
          selfInvite: "Du kannst dich nicht selbst einladen.",
          emailMismatch: "Diese Einladung gilt nicht für diese E-Mail-Adresse."
        },
        error: {
          generic:
            "Ein unerwarteter Fehler ist passiert, bitte versuche es später erneut.<br><br>Läuft es dann immer noch schief, schreib gern unserem Kundenservice: <a href='mailto:support@blinkist.com'>support@blinkist.com</a>",
          forbidden:
            "Du hast nicht die notwendigen Berechtigungen, um diese Seite aufzurufen.<br><br>Wenn du denkst du sollest hierauf Zugriff haben und das Problem noch länger besteht, schreib gern unserem Kundenservice: <a href='mailto:support@blinkist.com'>support@blinkist.com</a>",
          invalidInvite:
            "Dieser Einladungslink ist ungültig und kann nicht verwendet werden. <br /><br />Das sollte dich trotzdem nicht davon abhalten, dich von {totalBooks} Titeln inspirieren zu lassen!",
          inviteAlreadyUsed:
            "Dieser Einladungslink wurde bereits verwendet und ist nicht mehr gültig.<br><br>Das sollte dich trotzdem nicht davon abhalten, dich von {totalBooks} Titeln inspirieren zu lassen!",
          partOfOtherOrganisation:
            "Leider kannst du diese Einladung nicht annehmen, da du bereits Teil eines Team-Abos bist oder dir schon mit jemandem ein Abo teilst.<br><br>Das Abo kannst du natürlich weiter nutzen, um die Kernaussagen aus über {totalBooks} Sachbüchern zu lesen oder anzuhören!",
          spaceForB2BOnly:
            "Diese Einladung ist nur für Mitarbeiter eines Unternehmens gültig, du kannst leider nicht darauf zugreifen.<br><br><b>Nutze stattdessen deinen {accountType} Account</b>, um die besten Aha-Momente aus tausenden Sachbüchern und Podcasts zu entdecken!",
          spaceForB2COnly:
            "Diese Einladung ist nur für private Accounts gültig und nicht für Nutzer mit einem Blinkist Business-Account.<br><br><b>Nutze stattdessen deinen {accountType} Account</b>, um die besten Aha-Momente aus tausenden Sachbüchern und Podcasts zu entdecken!",
          spaceNotFound:
            "Dieser Einladungslink ist ungültig und kann nicht verwendet werden.<br><br>Das {accountType} Abo kannst du natürlich weiter nutzen, um die Kernaussagen aus über {totalBooks} Sachbüchern zu lesen oder anzuhören!",
          onlyForNewUsers:
            "Dieses Angebot gilt nur für neue Nutzer. Melde dich in deinem Account an, um weiter zu lernen!",
          blinkistAccountAlreadyLinked: "Es ist nicht möglich, dein Blinkist-Konto mit einem weiteren Go1-Account zu verknüpfen.",
          go1AccountAlreadyLinked: "Es ist nicht möglich, dein Go1-Konto mit einem weiteren Blinkist-Account zu verknüpfen.",
          ensureCorrectQrCode: "Stelle sicher, dass du den QR Code vom richtigen Go1-Konto verwendet hast. Versuche auch, die Seite mit dem QR code neu zu laden.",
          go1PortalBlocked: "Der Blinkist-Zugang über das Go1-Portal deiner Organisation wurde eingestellt, daher kann kein Premium-Zugang aktiviert werden. Bei weiteren Fragen kontaktiere uns bitte via support@blinkist.com.",
          selfInvite: "Du kannst deinen eigenen Einladungscode nicht verwenden, um deiner Organisation beizutreten.",
          emailMismatch: "Diese Einladung wurde an eine andere E-Mail-Adresse gesendet. Bitte verwenden Sie dieselbe E-Mail-Adresse, an die Sie die Einladung erhalten haben."
        },
        accountType: {
          basic: "Basic",
          premium: "Premium"
        }
      }
    }
  }
}
</script>
